import { useEffect, useRef, useState } from "react";
import type { RadarStateSource } from "@radar-sim/engine";
import { projectPosition } from "@radar-sim/engine";
import type {
  LatLon,
  VesselState,
  VesselType,
  WorldSnapshot,
} from "@radar-sim/schema";

/**
 * Mid-scenario vessel injector. Lets a student or instructor add
 * vessels of any type to a running scenario without having to author
 * a new scenario JSON. Each spawn picks a deterministic-but-varied
 * bearing offset around own ship and gives the vessel a sensible
 * default speed and heading-toward-own-ship, so the new traffic
 * shows up on the radar within a minute or two.
 *
 * Uses the existing `inject_event` command + `vessel_appears` event
 * pathway, so this works with any RadarStateSource — local sim,
 * classroom DO, or replay (replay rejects the command). No special
 * casing required in the engine.
 */

type SpawnPreset = {
  type: VesselType;
  state?: VesselState;
  label: string;
  rcs: number;
  sog: number;
};

const PRESETS: ReadonlyArray<SpawnPreset> = [
  { type: "yacht-sail", label: "Yacht (sail)", rcs: 0.18, sog: 5 },
  { type: "yacht-power", label: "Yacht (power)", rcs: 0.2, sog: 8 },
  { type: "rib", label: "RIB", rcs: 0.08, sog: 22 },
  { type: "fishing", label: "Fishing trawler", rcs: 0.25, sog: 4 },
  { type: "ferry", label: "Ferry", rcs: 0.9, sog: 12 },
  { type: "fastcat", label: "FastCat (HSC)", rcs: 0.55, sog: 22 },
  { type: "hovercraft", label: "Hovercraft (HSC)", rcs: 0.4, sog: 30 },
  { type: "container", label: "Container ship", rcs: 1, sog: 14 },
  {
    type: "ferry",
    state: "constrained-by-draught",
    label: "Large RoPax (CBD)",
    rcs: 0.95,
    sog: 12,
  },
  { type: "tug", state: "towing-long", label: "Tug + tow (long)", rcs: 0.4, sog: 6 },
  { type: "pilot", label: "Pilot vessel", rcs: 0.18, sog: 14 },
  {
    type: "minesweeper",
    state: "minesweeping",
    label: "Minesweeper (operating)",
    rcs: 0.3,
    sog: 4,
  },
  {
    type: "dredger",
    state: "dredging",
    label: "Dredger (operating)",
    rcs: 0.5,
    sog: 1,
  },
];

/** Pending-spawn handle exposed to canvases so they can deliver a
 *  click position when the picker is waiting for "where to drop". */
export interface PendingSpawn {
  preset: SpawnPreset;
  /** Called by the canvas click handler with the chosen world point. */
  fulfil: (at: LatLon) => void;
  /** Cancel the pending spawn (e.g. user picks a different preset). */
  cancel: () => void;
}

export function AddVesselPicker(props: {
  source: RadarStateSource;
  onPendingSpawn: (p: PendingSpawn | null) => void;
}): JSX.Element {
  const [snapshot, setSnapshot] = useState<WorldSnapshot | null>(null);
  const [counter, setCounter] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [placeMode, setPlaceMode] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<SpawnPreset | null>(null);
  // Click-to-place fulfilment happens *after* spawn() returns, when
  // the user taps the chart/radar. The fulfil closure captured by
  // SessionRunner at that moment would otherwise see a snapshot
  // frozen at picker-click time; if the user delays the tap by even
  // a few seconds the vessel_appears event ends up scheduled for a
  // sim-time the engine has already passed. Stash the latest
  // snapshot in a ref so spawnAt always reads the current sim time.
  const latestSnapshotRef = useRef<WorldSnapshot | null>(null);

  useEffect(() => {
    const stop = props.source.subscribe((s) => {
      setSnapshot(s);
      latestSnapshotRef.current = s;
    });
    return stop;
  }, [props.source]);

  const spawnAt = (preset: SpawnPreset, spawnPos: LatLon) => {
    const snap = latestSnapshotRef.current;
    if (snap === null) return;
    const cog = 180; // nominal — could be derived from chart click
    const endPos = projectPosition(spawnPos, cog, preset.sog * 0.5);
    const id = `spawned-${preset.type}-${counter}`;
    const startSec = snap.simTimeSeconds + 0.2;
    void props.source.sendCommand({
      kind: "inject_event",
      timed: {
        atSimSeconds: startSec,
        event: {
          kind: "vessel_appears",
          vessel: {
            kind: "tracked",
            id,
            vesselType: preset.type,
            rcs: preset.rcs,
            ...(preset.state !== undefined ? { state: preset.state } : {}),
            waypoints: [
              { atSimSeconds: 0, position: spawnPos, cog, sog: preset.sog },
              {
                atSimSeconds: Math.max(1, startSec),
                position: spawnPos,
                cog,
                sog: preset.sog,
              },
              {
                atSimSeconds: Math.max(2, startSec) + 1800,
                position: endPos,
                cog,
                sog: preset.sog,
              },
            ],
          },
        },
      },
    });
    setCounter((c) => c + 1);
    setPendingPreset(null);
    props.onPendingSpawn(null);
  };

  const spawn = (preset: SpawnPreset) => {
    if (snapshot === null) return;
    if (placeMode) {
      // Click-to-place mode: defer the spawn until the user taps the
      // chart or radar. Bubble the pending intent up to SessionRunner
      // which routes the click into spawnAt.
      setPendingPreset(preset);
      props.onPendingSpawn({
        preset,
        fulfil: (at) => spawnAt(preset, at),
        cancel: () => {
          setPendingPreset(null);
          props.onPendingSpawn(null);
        },
      });
      setPickerOpen(false);
      return;
    }
    const own = snapshot.ownShip.position;
    // Scatter spawns around own ship using a coprime-step rotation
    // so successive adds land on different bearings without
    // randomness. 137° is the "golden angle" — gives well-separated
    // bearings even for many adds.
    const bearing = (counter * 137 + 30) % 360;
    const rangeNm = 1.2 + (counter % 4) * 0.6;
    const spawnPos = projectPosition(own, bearing, rangeNm);
    // Default course: head roughly toward own ship's path so a
    // student gets an interesting encounter rather than drifting away.
    const cog = (bearing + 180) % 360;
    const endPos = projectPosition(spawnPos, cog, preset.sog * 0.5);
    const id = `spawned-${preset.type}-${counter}`;
    const startSec = snapshot.simTimeSeconds + 0.2;
    void props.source.sendCommand({
      kind: "inject_event",
      timed: {
        atSimSeconds: startSec,
        event: {
          kind: "vessel_appears",
          vessel: {
            kind: "tracked",
            id,
            vesselType: preset.type,
            rcs: preset.rcs,
            ...(preset.state !== undefined ? { state: preset.state } : {}),
            // Waypoints must start at simSeconds 0 per schema. Hold at
            // spawn until the spawn sim-time, then transit toward the
            // end position over the next 30 minutes.
            waypoints: [
              { atSimSeconds: 0, position: spawnPos, cog, sog: preset.sog },
              {
                atSimSeconds: Math.max(1, startSec),
                position: spawnPos,
                cog,
                sog: preset.sog,
              },
              {
                atSimSeconds: Math.max(2, startSec) + 1800,
                position: endPos,
                cog,
                sog: preset.sog,
              },
            ],
          },
        },
      },
    });
    setCounter((c) => c + 1);
    setPickerOpen(false);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: 10,
      }}
    >
      {pendingPreset !== null && (
        <div
          style={{
            background: "rgba(56, 189, 248, 0.12)",
            border: "1px solid var(--accent)",
            borderRadius: "var(--radius-sm)",
            padding: "8px 10px",
            fontSize: 11,
            color: "var(--text)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span>
            Tap chart or radar to place{" "}
            <strong>{pendingPreset.label}</strong>…
          </span>
          <button
            onClick={() => {
              setPendingPreset(null);
              props.onPendingSpawn(null);
            }}
            style={{
              background: "transparent",
              color: "var(--text-muted)",
              border: "none",
              cursor: "pointer",
              fontSize: 14,
            }}
            aria-label="Cancel pending spawn"
          >
            ×
          </button>
        </div>
      )}
      <button
        onClick={() => setPickerOpen((o) => !o)}
        aria-expanded={pickerOpen}
        style={{
          background: "transparent",
          color: "var(--text)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-sm)",
          padding: "8px 10px",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span>+ Add vessel</span>
        <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
          {pickerOpen ? "▲" : "▼"}
        </span>
      </button>
      {pickerOpen && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: "var(--text-muted)",
            cursor: "pointer",
            padding: "2px 0",
          }}
        >
          <input
            type="checkbox"
            checked={placeMode}
            onChange={(e) => setPlaceMode(e.target.checked)}
          />
          Click-to-place (tap chart/radar to choose position)
        </label>
      )}
      {pickerOpen && (
        <div
          role="menu"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 4,
          }}
        >
          {PRESETS.map((p) => (
            <button
              key={p.label}
              role="menuitem"
              onClick={() => spawn(p)}
              style={{
                background: "var(--bg-elev)",
                color: "var(--text)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
                padding: "6px 8px",
                fontSize: 11,
                cursor: "pointer",
                textAlign: "left",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
